import { useMemo } from 'react';
import useProfile from './useProfile';
import { Permission } from 'services/types/permissions';

interface UsePermissionsReturn {
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  userPermissions: Permission[];
}
const ADMIN_ROLE = 'ADMIN_ROLE';

const usePermissions = (): UsePermissionsReturn => {
  const { profile } = useProfile();

  const userPermissions = useMemo(() => {
    if (!profile || !profile['cognito:groups']) {
      return [];
    }

    // cognito:groups can be a string or array of strings
    const groups = Array.isArray(profile['cognito:groups'])
      ? profile['cognito:groups']
      : [profile['cognito:groups']];

    return groups;
  }, [profile]);

  const hasPermission = (permission: Permission): boolean => {
    // Admin role has access to all permissions
    if (userPermissions.includes(ADMIN_ROLE)) {
      return true;
    }
    return userPermissions.includes(permission);
  };

  const hasAnyPermission = (permissions: Permission[]): boolean => {
    // Admin role has access to all permissions
    if (userPermissions.includes(ADMIN_ROLE)) {
      return true;
    }
    return permissions.some((permission) => userPermissions.includes(permission));
  };

  const hasAllPermissions = (permissions: Permission[]): boolean => {
    // Admin role has access to all permissions
    if (userPermissions.includes(ADMIN_ROLE)) {
      return true;
    }
    return permissions.every((permission) => userPermissions.includes(permission));
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    userPermissions,
  };
};

export default usePermissions;
